import cn from 'classnames';
import { forwardRef } from 'react';

import { CloseIcon } from '../../../foundations';
import { ModalCloseButton } from '../modal-close-button';
import { useModalContext } from '../modal-context';

import type { ForwardedRef, ReactElement } from 'react';

import type { ModalHeaderProps } from './modal-header.types';

import './modal-header.scss';

/**
 * Modal header component is used to render the header section of the modal dialog.
 * If `onClose` is provided to the `Modal` component, then a close button is rendered in the header section.
 * Use `ModalHeaderTitle` component to render the title in the header section.
 */
const ModalHeader = forwardRef((props: ModalHeaderProps, forwardedRef: ForwardedRef<HTMLDivElement>): ReactElement => {
  const { children, className, ...rest } = props;

  const modalContext = useModalContext();
  if (modalContext === null) {
    throw new Error('ModalHeader must be used within a ModalContext.Provider');
  }

  const { onClose } = modalContext;

  return (
    <div {...rest} className={cn('rbui-modal__header', className)} ref={forwardedRef}>
      <div className="rbui-modal__header-section">{children}</div>
      {onClose && (
        <ModalCloseButton className="rbui-modal__header-close" onClick={() => onClose?.()} startIcon={<CloseIcon />} />
      )}
    </div>
  );
});

ModalHeader.displayName = 'ModalHeader';

export default ModalHeader;
